/**
 * Financial API Client
 * Fetches financial data from InstantDB for the AI Gateway dashboard
 */

import { db, useQuery } from "@/lib/instantdb";
import type {
  SubscriptionTier,
  Subscription,
  ApiCost,
  MonthlySummary,
  ApiProvider,
} from "@/lib/instantdb";

// ============ Computed Metrics Interfaces ============

export interface FinancialOverview {
  mrr: number; // Monthly Recurring Revenue in cents
  netProfit: number; // Revenue - Costs in cents
  grossMargin: number; // (Revenue - Costs) / Revenue as percentage
  arpu: number; // Average Revenue Per User in cents
  activeSubscribers: number;
  totalCosts: number; // in cents
  totalRevenue: number; // in cents
  periodStart: string;
  periodEnd: string;
}

export interface CostBreakdown {
  byProvider: Array<{
    provider: ApiProvider;
    cost: number; // in cents
    percentage: number;
    requests: number;
  }>;
  byModel: Array<{
    model: string;
    provider: ApiProvider;
    cost: number;
    tokens: number;
  }>;
  totalCost: number;
  totalRequests: number; // sum of all API requests
}

export interface RevenueTrend {
  monthly: Array<{
    month: string;
    revenue: number;
    costs: number;
    netProfit: number;
    subscribers: number;
    videos: number;
  }>;
}

export interface SubscriptionBreakdown {
  byTier: Array<{
    tierName: string;
    count: number;
    percentage: number;
    revenue: number;
  }>;
  churnRate: number;
  newSubscribers: number;
  cancelledSubscribers: number;
}

// ============ InstantDB Hooks ============

/**
 * Hook to get all subscription tiers
 */
export function useSubscriptionTiers() {
  return useQuery({
    subscription_tiers: {
      $: {
        where: { isActive: true },
        order: { monthlyPriceCentsCents: "asc" },
      },
    },
  });
}

/**
 * Hook to get all active subscriptions with tier info
 */
export function useActiveSubscriptions() {
  return useQuery({
    subscriptions: {
      $: {
        where: { status: "active" },
      },
      tier: {},
    },
  });
}

/**
 * Hook to get API costs for a time period
 */
export function useApiCosts(days = 30) {
  const startDate = Date.now() - days * 24 * 60 * 60 * 1000;

  return useQuery({
    api_costs: {
      $: {
        where: { timestamp: { $gt: startDate } },
        order: { timestamp: "desc" },
      },
    },
  });
}

/**
 * Hook to get monthly summaries
 */
export function useMonthlySummaries(months = 12) {
  // Get summaries for the last N months
  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - months);
  const cutoffMonth = cutoffDate.toISOString().slice(0, 7);

  return useQuery({
    monthly_summaries: {
      $: {
        where: { month: { $gte: cutoffMonth } },
        order: { month: "asc" },
      },
    },
  });
}

/**
 * Combined hook for financial dashboard data
 */
export function useFinancialDashboard(days = 30) {
  const startDate = Date.now() - days * 24 * 60 * 60 * 1000;

  return useQuery({
    subscription_tiers: {
      $: { where: { isActive: true } },
    },
    subscriptions: {
      $: { where: { status: "active" } },
      tier: {},
    },
    api_costs: {
      $: {
        where: { timestamp: { $gt: startDate } },
        order: { timestamp: "desc" },
      },
    },
    monthly_summaries: {
      $: { order: { month: "desc" }, limit: 12 },
    },
  });
}

// ============ One-time Queries ============

/**
 * One-time query to get subscription tiers
 */
export async function getSubscriptionTiers(): Promise<SubscriptionTier[]> {
  const { data } = await db.queryOnce({
    subscription_tiers: {
      $: {
        where: { isActive: true },
        order: { monthlyPriceCents: "asc" },
      },
    },
  });

  return (data?.subscription_tiers ?? []) as SubscriptionTier[];
}

/**
 * One-time query to get active subscriptions
 */
export async function getActiveSubscriptions(): Promise<Subscription[]> {
  const { data } = await db.queryOnce({
    subscriptions: {
      $: { where: { status: "active" } },
      tier: {},
    },
  });

  return (data?.subscriptions ?? []) as Subscription[];
}

/**
 * One-time query to get API costs
 */
export async function getApiCosts(days = 30): Promise<ApiCost[]> {
  const startDate = Date.now() - days * 24 * 60 * 60 * 1000;

  const { data } = await db.queryOnce({
    api_costs: {
      $: {
        where: { timestamp: { $gt: startDate } },
        order: { timestamp: "desc" },
      },
    },
  });

  return (data?.api_costs ?? []) as ApiCost[];
}

/**
 * One-time query to get monthly summaries
 */
export async function getMonthlySummaries(
  months = 12,
): Promise<MonthlySummary[]> {
  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - months);
  const cutoffMonth = cutoffDate.toISOString().slice(0, 7);

  const { data } = await db.queryOnce({
    monthly_summaries: {
      $: {
        where: { month: { $gte: cutoffMonth } },
        order: { month: "asc" },
      },
    },
  });

  return (data?.monthly_summaries ?? []) as MonthlySummary[];
}

// ============ Computed Metrics ============

/**
 * Compute financial overview from subscriptions and costs
 */
export function computeFinancialOverview(
  subscriptions: Subscription[],
  tiers: SubscriptionTier[],
  costs: ApiCost[],
  days = 30,
): FinancialOverview {
  const now = Date.now();
  const periodStart = now - days * 24 * 60 * 60 * 1000;

  // Create tier lookup
  const tierMap = new Map(tiers.map((t) => [t.id, t]));

  // Calculate MRR from active subscriptions
  let mrr = 0;
  for (const sub of subscriptions) {
    const tier = tierMap.get(sub.tierId);
    if (tier) {
      mrr += tier.monthlyPriceCents;
    }
  }

  // Calculate total costs
  const totalCosts = costs.reduce((sum, c) => sum + c.cost, 0);

  // Calculate revenue (MRR for the period)
  const totalRevenue = mrr;

  // Calculate net profit
  const netProfit = totalRevenue - totalCosts;

  // Calculate gross margin
  const grossMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  // Calculate ARPU
  const activeSubscribers = subscriptions.length;
  const arpu = activeSubscribers > 0 ? mrr / activeSubscribers : 0;

  return {
    mrr,
    netProfit,
    grossMargin: Math.round(grossMargin * 10) / 10, // Round to 1 decimal
    arpu: Math.round(arpu),
    activeSubscribers,
    totalCosts,
    totalRevenue,
    periodStart: new Date(periodStart).toISOString(),
    periodEnd: new Date(now).toISOString(),
  };
}

/**
 * Compute cost breakdown by provider and model
 */
export function computeCostBreakdown(costs: ApiCost[]): CostBreakdown {
  const providerCosts: Record<string, { cost: number; requests: number }> = {};
  const modelCosts: Record<
    string,
    { cost: number; tokens: number; provider: ApiProvider }
  > = {};

  for (const cost of costs) {
    // By provider
    if (!providerCosts[cost.provider]) {
      providerCosts[cost.provider] = { cost: 0, requests: 0 };
    }
    providerCosts[cost.provider].cost += cost.cost;
    providerCosts[cost.provider].requests += 1;

    // By model
    const modelKey = `${cost.provider}:${cost.model}`;
    if (!modelCosts[modelKey]) {
      modelCosts[modelKey] = {
        cost: 0,
        tokens: 0,
        provider: cost.provider as ApiProvider,
      };
    }
    modelCosts[modelKey].cost += cost.cost;
    modelCosts[modelKey].tokens += cost.tokens;
  }

  const totalCost = costs.reduce((sum, c) => sum + c.cost, 0);
  const totalRequests = Object.values(providerCosts).reduce(
    (sum, data) => sum + data.requests,
    0,
  );

  return {
    byProvider: Object.entries(providerCosts)
      .map(([provider, data]) => ({
        provider: provider as ApiProvider,
        cost: data.cost,
        percentage:
          totalCost > 0 ? Math.round((data.cost / totalCost) * 100) : 0,
        requests: data.requests,
      }))
      .sort((a, b) => b.cost - a.cost),
    byModel: Object.entries(modelCosts)
      .map(([key, data]) => ({
        model: key.split(":")[1],
        provider: data.provider,
        cost: data.cost,
        tokens: data.tokens,
      }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 10),
    totalCost,
    totalRequests,
  };
}

/**
 * Compute subscription breakdown by tier
 */
export function computeSubscriptionBreakdown(
  subscriptions: Subscription[],
  tiers: SubscriptionTier[],
  previousMonthSubscriptions?: Subscription[],
): SubscriptionBreakdown {
  const tierMap = new Map(tiers.map((t) => [t.id, t]));
  const tierCounts: Record<string, { count: number; revenue: number }> = {};

  // Initialize all tiers
  for (const tier of tiers) {
    tierCounts[tier.name] = { count: 0, revenue: 0 };
  }

  // Count subscriptions by tier
  for (const sub of subscriptions) {
    const tier = tierMap.get(sub.tierId);
    if (tier) {
      tierCounts[tier.name].count += 1;
      tierCounts[tier.name].revenue += tier.monthlyPriceCents;
    }
  }

  const total = subscriptions.length || 1;

  // Calculate churn (if previous month data available)
  let churnRate = 0;
  let newSubscribers = 0;
  let cancelledSubscribers = 0;

  if (previousMonthSubscriptions) {
    const prevUserIds = new Set(
      previousMonthSubscriptions.map((s) => s.userId),
    );
    const currentUserIds = new Set(subscriptions.map((s) => s.userId));

    // New subscribers = users in current but not in previous
    for (const userId of currentUserIds) {
      if (!prevUserIds.has(userId)) {
        newSubscribers++;
      }
    }

    // Cancelled = users in previous but not in current
    for (const userId of prevUserIds) {
      if (!currentUserIds.has(userId)) {
        cancelledSubscribers++;
      }
    }

    churnRate =
      prevUserIds.size > 0
        ? Math.round((cancelledSubscribers / prevUserIds.size) * 100)
        : 0;
  }

  return {
    byTier: Object.entries(tierCounts)
      .map(([tierName, data]) => ({
        tierName,
        count: data.count,
        percentage: Math.round((data.count / total) * 100),
        revenue: data.revenue,
      }))
      .sort((a, b) => b.revenue - a.revenue),
    churnRate,
    newSubscribers,
    cancelledSubscribers,
  };
}

/**
 * Compute revenue trend from monthly summaries
 */
export function computeRevenueTrend(summaries: MonthlySummary[]): RevenueTrend {
  return {
    monthly: summaries.map((s) => ({
      month: s.month,
      revenue: s.totalRevenue,
      costs: s.totalCosts,
      netProfit: s.totalRevenue - s.totalCosts,
      subscribers: s.activeSubscribers,
      videos: s.videosProduced,
    })),
  };
}
